import { useState, useRef, useEffect, useMemo } from 'react';

// Reusable people/option picker: A-Z sorted + type-to-search.
// Drop-in for any long <select> of people. Pass options as [{ value, label }].
// Props: value, onChange(value), options, placeholder, disabled, style.
const T = {
  field: '#2a1f0f', line: '#3a2a12', cream: '#f5e6c8', gold: '#c9a96e',
  card: '#1a1208', dim: 'rgba(245,230,200,0.6)', activeBg: '#c9a96e18',
};

export default function SearchableSelect({ value, onChange, options = [], placeholder = 'Choose…', disabled = false, style = {} }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // A-Z sort once, then filter by the search query (case-insensitive substring).
  const sorted = useMemo(
    () => options.slice().sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' })),
    [options]
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? sorted.filter(o => String(o.label).toLowerCase().includes(q)) : sorted;
  }, [sorted, query]);

  const selected = options.find(o => String(o.value) === String(value));

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDoc(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Focus the search box when the menu opens.
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  function choose(v) { onChange(v); setOpen(false); setQuery(''); }

  const fieldStyle = {
    width: '100%', background: T.field, border: '1px solid ' + T.line, borderRadius: 8,
    padding: '10px 12px', color: selected ? T.cream : T.dim, fontSize: 14, boxSizing: 'border-box',
    fontFamily: 'inherit', textAlign: 'left', cursor: disabled ? 'default' : 'pointer',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, opacity: disabled ? 0.5 : 1,
  };

  return (
    <div ref={wrapRef} style={Object.assign({ position: 'relative' }, style)}>
      <button type="button" disabled={disabled} onClick={() => !disabled && setOpen(o => !o)} style={fieldStyle}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected ? selected.label : placeholder}</span>
        <span style={{ color: T.dim, fontSize: 11, flexShrink: 0 }}>▾</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', zIndex: 50, top: 'calc(100% + 4px)', left: 0, right: 0, background: T.card, border: '1px solid ' + T.line, borderRadius: 10, boxShadow: '0 16px 50px rgba(0,0,0,0.6)', overflow: 'hidden' }}>
          <div style={{ padding: 8, borderBottom: '1px solid ' + T.line }}>
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') { setOpen(false); setQuery(''); }
                else if (e.key === 'Enter' && filtered.length) { e.preventDefault(); choose(filtered[0].value); }
              }}
              placeholder="Search…"
              style={{ width: '100%', background: T.field, border: '1px solid ' + T.line, borderRadius: 6, padding: '8px 10px', color: T.cream, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '12px', color: T.dim, fontSize: 13 }}>No matches</div>
            ) : filtered.map(o => {
              const isSel = String(o.value) === String(value);
              return (
                <div
                  key={o.value}
                  onClick={() => choose(o.value)}
                  style={{ padding: '9px 12px', cursor: 'pointer', fontSize: 14, color: isSel ? T.gold : T.cream, background: isSel ? T.activeBg : 'transparent', fontWeight: isSel ? 700 : 400 }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#2a1f0f'; }}
                  onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
                >
                  {o.label}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
